// The business's name and logo, remembered on this device so pre-login screens
// (main app splash/login, techs app, customer portal) can paint them before
// /settings/businessProfile has loaded — no flash of placeholder text or a
// stock logo. Each page's inline boot script reads the same key and fills in
// [data-brand="name"] / [data-brand="logo"] elements; paintBrand() does it
// again once the live profile arrives.

const KEY = 'businessProfileCache';
// The logo itself (a small data: URL), so the splash shows it with no network —
// the Storage URL alone needs a download every cold start and fails offline.
const LOGO_KEY = 'businessLogoCache';

/** The logo to show: the copy on this device when it's the current one, else the URL. */
export const brandLogo = url => (url && storedLogo(url)) || url || '';

function storedLogo(url) {
  try {
    const c = JSON.parse(localStorage.getItem(LOGO_KEY) || 'null');
    return c && c.url === url && c.data ? c.data : null;
  } catch { return null; }
}

// Download the logo once per URL and keep a 256 px copy on the device.
async function keepLogo(url) {
  if (!url || storedLogo(url)) return;
  try {
    const blob = await (await fetch(url, { mode: 'cors' })).blob();
    const bmp = await createImageBitmap(blob);
    const k = Math.min(1, 256 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * k);
    c.height = Math.round(bmp.height * k);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    let data = c.toDataURL('image/webp', 0.9);
    if (!data.startsWith('data:image/webp')) data = c.toDataURL('image/png');
    localStorage.setItem(LOGO_KEY, JSON.stringify({ url, data }));
  } catch (e) { console.warn('[brand] could not keep the logo on this device', e); }
}

export function paintBrand(profile) {
  if (!profile) return;
  if (profile.name) {
    document.querySelectorAll('[data-brand="name"]').forEach(el => { el.textContent = profile.name; });
  }
  document.querySelectorAll('[data-brand="logo"]').forEach(el => {
    if (profile.logoUrl) {
      const src = storedLogo(profile.logoUrl) || profile.logoUrl;
      if (el.getAttribute('src') !== src) el.src = src;
    }
    el.style.visibility = 'visible';
  });
  // The office phone (Settings › Business): [data-brand="phone"] shows it, [data-brand-tel]
  // links to it, [data-brand-has="phone"] only shows when there is one.
  const phone = String(profile.phone || '').trim();
  const tel = phone.replace(/[^\d+]/g, '');
  document.querySelectorAll('[data-brand="phone"]').forEach(el => { el.textContent = phone; });
  document.querySelectorAll('[data-brand-tel]').forEach(el => { if (tel) el.setAttribute('href', `tel:${tel}`); });
  document.querySelectorAll('[data-brand-has="phone"]').forEach(el => { el.hidden = !tel; });
}

/** Remember the live profile and repaint with it. */
export function rememberBrand(profile) {
  if (!profile) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({
      name: profile.name || '', logoUrl: profile.logoUrl || '',
      address: profile.address || '', phone: profile.phone || '', supportEmail: profile.supportEmail || '',
    }));
  } catch { /* storage blocked */ }
  paintBrand(profile);
  keepLogo(profile.logoUrl);
}

export function cachedBrand() {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
}
